// Embedded hardware catalog for the Peripheral & Telemetry Matrix.
// Passive wearables are synced through the Vital/Junction gateway; gym
// machinery streams locally over BLE FTMS / ANT+ FE-C / PM5.

import type { MachineKind, TelemetryMetric, WearableCatalogEntry } from './types'

// ---------------------------------------------------------------------------
// BLE / GATT identifiers
// ---------------------------------------------------------------------------

/** Fitness Machine Service — 16-bit UUID 0x1826. */
export const FTMS_SERVICE_UUID = 0x1826
/** Heart Rate Service — 0x180D, Heart Rate Measurement char — 0x2A37. */
export const HEART_RATE_SERVICE_UUID = 0x180d
export const HEART_RATE_MEASUREMENT_UUID = 0x2a37

/** FTMS data characteristics keyed by machine kind. */
export const FTMS_DATA_CHARACTERISTICS: Record<string, { uuid: number; kind: MachineKind }> = {
  treadmill: { uuid: 0x2acd, kind: 'treadmill' },
  cross_trainer: { uuid: 0x2ace, kind: 'elliptical' },
  step_climber: { uuid: 0x2acf, kind: 'stair_climber' },
  stair_climber: { uuid: 0x2ad0, kind: 'stair_climber' },
  rower: { uuid: 0x2ad1, kind: 'rower' },
  indoor_bike: { uuid: 0x2ad2, kind: 'indoor_bike' },
}

export const FTMS_CONTROL_POINT_UUID = 0x2ad9
export const FTMS_MACHINE_FEATURE_UUID = 0x2acc

/**
 * Concept2 PM5 proprietary GATT (base UUID CE060000-43E5-11E4-916C-0800200C9A66).
 * Rowing telemetry is multiplexed across these characteristics.
 */
export const PM5_SERVICE_UUID = 'ce060030-43e5-11e4-916c-0800200c9a66'
export const PM5_CHARACTERISTICS = {
  generalStatus: 'ce060031-43e5-11e4-916c-0800200c9a66',
  additionalStatus1: 'ce060032-43e5-11e4-916c-0800200c9a66', // speed, stroke rate, pace, hr
  additionalStatus2: 'ce060033-43e5-11e4-916c-0800200c9a66', // power (watts), calories
  strokeData: 'ce060035-43e5-11e4-916c-0800200c9a66', // per-stroke distance/force/power
} as const

/** Velocity-Based-Training proprietary brands (no universal standard). */
export const VBT_PROPRIETARY_BRANDS = ['vitruvian', 'tonal', 'gymaware', 'push_band', 'vmaxpro'] as const

// ---------------------------------------------------------------------------
// Per-machine streamed metrics (drives UI capability badges)
// ---------------------------------------------------------------------------

export const MACHINE_CAPABILITIES: Record<MachineKind, TelemetryMetric[]> = {
  treadmill: ['speed_kmh', 'incline_pct', 'heart_rate_bpm', 'calories_kcal', 'distance_m', 'elapsed_s'],
  indoor_bike: ['power_watts', 'cadence_rpm', 'resistance_level', 'heart_rate_bpm', 'calories_kcal', 'elapsed_s'],
  rower: ['power_watts', 'stroke_rate', 'split_500m_sec', 'distance_m', 'heart_rate_bpm', 'calories_kcal', 'elapsed_s'],
  elliptical: ['stride_rate', 'cadence_rpm', 'resistance_level', 'heart_rate_bpm', 'calories_kcal', 'elapsed_s'],
  stair_climber: ['stride_rate', 'cadence_rpm', 'heart_rate_bpm', 'calories_kcal', 'elapsed_s'],
  ski_erg: ['power_watts', 'stroke_rate', 'distance_m', 'calories_kcal', 'elapsed_s'],
  vbt_bar: ['mean_velocity_ms', 'peak_velocity_ms', 'load_kg', 'rep_count', 'time_under_tension_s'],
  unknown: ['heart_rate_bpm', 'elapsed_s'],
}

/** The single "gold" metric to headline per machine kind on the live HUD. */
export const PRIMARY_METRIC: Record<MachineKind, TelemetryMetric> = {
  treadmill: 'speed_kmh',
  indoor_bike: 'power_watts',
  rower: 'power_watts',
  ski_erg: 'power_watts',
  elliptical: 'cadence_rpm',
  stair_climber: 'stride_rate',
  vbt_bar: 'mean_velocity_ms',
  unknown: 'heart_rate_bpm',
}

export const METRIC_LABELS: Record<TelemetryMetric, { label: string; unit: string }> = {
  speed_kmh: { label: 'Speed', unit: 'km/h' },
  incline_pct: { label: 'Incline', unit: '%' },
  cadence_rpm: { label: 'Cadence', unit: 'rpm' },
  resistance_level: { label: 'Resistance', unit: 'lvl' },
  stride_rate: { label: 'Stride Rate', unit: 'spm' },
  power_watts: { label: 'Power', unit: 'W' },
  stroke_rate: { label: 'Stroke Rate', unit: 'spm' },
  split_500m_sec: { label: 'Split /500m', unit: '' },
  distance_m: { label: 'Distance', unit: 'm' },
  mean_velocity_ms: { label: 'Mean Velocity', unit: 'm/s' },
  peak_velocity_ms: { label: 'Peak Velocity', unit: 'm/s' },
  load_kg: { label: 'Load', unit: 'kg' },
  rep_count: { label: 'Reps', unit: '' },
  time_under_tension_s: { label: 'TUT', unit: 's' },
  heart_rate_bpm: { label: 'Heart Rate', unit: 'bpm' },
  calories_kcal: { label: 'Calories', unit: 'kcal' },
  elapsed_s: { label: 'Elapsed', unit: 's' },
}

// ---------------------------------------------------------------------------
// Passive wearable catalog (synced via Vital/Junction)
// ---------------------------------------------------------------------------

export const WEARABLE_CATALOG: WearableCatalogEntry[] = [
  // Tier 1 — full coaching data (HRV, sleep, strain, readiness)
  { brand_slug: 'apple_watch', name: 'Apple Watch', tier: 1, models: ['Series 9', 'Ultra 2', 'SE'] },
  { brand_slug: 'oura', name: 'Oura Ring', tier: 1, models: ['Gen 3', 'Gen 4'] },
  { brand_slug: 'garmin', name: 'Garmin', tier: 1, models: ['Fenix', 'Forerunner', 'Venu'] },
  { brand_slug: 'whoop', name: 'Whoop', tier: 1, models: ['4.0', '5.0'] },
  { brand_slug: 'polar', name: 'Polar', tier: 1, models: ['H10', 'Vantage V3'] },
  { brand_slug: 'samsung_galaxy_watch', name: 'Samsung Galaxy Watch', tier: 1, models: ['5', '6', '7', 'Ultra'] },
  { brand_slug: 'dexcom', name: 'Dexcom', tier: 1, models: ['G7', 'ONE+'] },
  { brand_slug: 'abbott_librelink', name: 'Abbott LibreLink', tier: 1, models: ['Libre 3'] },
  { brand_slug: 'eight_sleep', name: 'Eight Sleep', tier: 1, models: ['Pod 3', 'Pod 4'] },
  { brand_slug: 'dreem', name: 'Dreem 3', tier: 1, models: ['3'] },
  { brand_slug: 'biostrap', name: 'Biostrap EVO', tier: 1, models: ['EVO'] },
  { brand_slug: 'hexoskin', name: 'Hexoskin', tier: 1, models: ['Smart'] },
  { brand_slug: 'ultrahuman', name: 'Ultrahuman Ring Air', tier: 1, models: ['Ring Air'] },

  // Tier 2 — good supporting data (sleep, basic activity, metrics)
  { brand_slug: 'fitbit', name: 'Fitbit', tier: 2, models: ['Sense 2', 'Versa 4', 'Charge 6'] },
  { brand_slug: 'withings', name: 'Withings', tier: 2, models: ['ScanWatch 2', 'Body+'] },
  { brand_slug: 'google_pixel_watch', name: 'Google Pixel Watch', tier: 2, models: ['2', '3'] },
  { brand_slug: 'suunto', name: 'Suunto', tier: 2, models: ['Race', 'Vertical'] },
  { brand_slug: 'amazfit', name: 'Amazfit', tier: 2, models: ['GTR 4'] },
  { brand_slug: 'huawei_watch', name: 'Huawei Watch', tier: 2, models: ['GT 4', 'GT 5'] },
  { brand_slug: 'coros', name: 'Coros', tier: 2, models: ['Apex 2 Pro'] },
  { brand_slug: 'omron', name: 'Omron HeartGuide', tier: 2, models: ['HeartGuide'] },
  { brand_slug: 'garmin_index', name: 'Garmin Index S2', tier: 2, models: ['S2'] },
  { brand_slug: 'movano_evie', name: 'Movano Evie Ring', tier: 2, models: ['Evie'] },

  // Tier 3 — basic activity / static platform syncs
  { brand_slug: 'xiaomi_mi_band', name: 'Xiaomi / Mi Band', tier: 3, models: ['8', '9'] },
  { brand_slug: 'peloton_band', name: 'Peloton Band', tier: 3, models: ['Band'] },
  { brand_slug: 'fitbit_aria', name: 'Fitbit Aria', tier: 3, models: ['Aria'] },
  { brand_slug: 'renpho', name: 'Renpho Smart Scale', tier: 3, models: ['Smart'] },
  { brand_slug: 'cronometer', name: 'Cronometer', tier: 3, models: ['App'] },
  { brand_slug: 'myfitnesspal', name: 'MyFitnessPal', tier: 3, models: ['App'] },
  { brand_slug: 'peloton', name: 'Peloton App', tier: 3, models: ['App'] },
  { brand_slug: 'strava', name: 'Strava', tier: 3, models: ['App'] },
  { brand_slug: 'apple_health', name: 'Apple Health', tier: 3, models: ['Platform'] },
  { brand_slug: 'google_health_connect', name: 'Google Health Connect', tier: 3, models: ['Platform'] },
]

export function wearablesByTier(tier: DeviceTierFilter): WearableCatalogEntry[] {
  if (tier === 'all') return WEARABLE_CATALOG
  return WEARABLE_CATALOG.filter((w) => w.tier === tier)
}

export type DeviceTierFilter = 1 | 2 | 3 | 'all'
